// frontend/src/features/campaign/components/create/Step6Governance.js
import React, { useState, useMemo } from 'react';
import { GOVERNANCE_GATES } from '../../constants/campaignConstants';
import { checkPauseApprovalRequirement } from '../../utils/campaignUtils';
import CertificateVerificationModal from './CertificateVerificationModal';

export default function Step6Governance({
  formData,
  calculatedKPIs,
  currentUserName,
  userRoleLabel,
  workspaceName,
  canDirectApprove,
  isEditMode = false,
  isReadOnly = false,
  initialCampaign = null,
  approvedGates,
  onToggleGate,
  onRequestChanges,
  signatureMode,
  setSignatureMode,
  typedSignature,
  setTypedSignature,
  hasDrawnSignature,
  isPolicyCertified,
  setIsPolicyCertified,
  isScanning,
  signatureCanvasRef,
  onStartDraw,
  onDraw,
  onStopDraw,
  onClearSignature,
  onTriggerBiometricScan,
  onFinalize,
  onNavigateToStep,
  isSaving = false,
}) {
  const isApprovedOrActive =
    initialCampaign?.status === 'ACTIVE' ||
    initialCampaign?.status === 'APPROVED' ||
    initialCampaign?.status === 'PAUSED' ||
    Boolean(
      initialCampaign?.governance?.allGatesApproved && initialCampaign?.governance?.isCertified
    );

  const isPendingApproval = initialCampaign?.status === 'PENDING_APPROVAL';

  const importedLeadsCount = (formData.importedLeads && formData.importedLeads.length) || 0;
  const hasLeads = importedLeadsCount > 0;
  const hasCompleteMatrix =
    Boolean(formData?.positioning?.trim()) &&
    Array.isArray(formData?.matrix) &&
    formData.matrix.length > 0 &&
    formData.matrix.every(
      (m) =>
        Boolean((m.persona || '').trim()) &&
        Boolean((m.painPoint || '').trim()) &&
        Boolean((m.valueProp || m.valueProposition || '').trim()) &&
        Boolean((m.proofPoint || '').trim()) &&
        Boolean((m.cta || '').trim()) &&
        Boolean((m.objection || '').trim())
    );
  const firstStep = formData.journey && formData.journey[0];
  const firstStepChannel =
    firstStep?.channel || (formData.channels && formData.channels[0]) || 'Email';
  const isChannelSupported = /email|whatsapp/i.test(firstStepChannel);

  // Step 6 Governance is locked only if:
  // 1. Campaign is already officially active/approved, OR
  // 2. User is a standard member without direct approval rights viewing a pending campaign
  // For Admins / Owners / Managers (canDirectApprove = true), pending campaigns MUST remain interactive to verify gates and sign off.
  const isLocked =
    isApprovedOrActive || (!canDirectApprove && initialCampaign?.status === 'PENDING_APPROVAL');

  const certId =
    initialCampaign?.governance?.certId ||
    (initialCampaign?.id
      ? `GOV-CERT-2026-${String(initialCampaign.id).padStart(6, '0')}`
      : 'GOV-CERT-2026-904128');

  const certHash =
    initialCampaign?.governance?.certHash ||
    'sha256:7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069';

  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);

  const proposerName =
    initialCampaign?.createdBy || formData.createdBy || 'Sarah Lim (Sales Member)';

  let approverNameRaw =
    initialCampaign?.status === 'PAUSED' && initialCampaign?.pausedBy
      ? initialCampaign.pausedBy
      : initialCampaign?.approvedBy ||
        initialCampaign?.governance?.signedBy ||
        formData?.governance?.signedBy ||
        (canDirectApprove
          ? `${currentUserName} (${userRoleLabel || 'Sales Manager'})`
          : 'Jonas Low (Sales Manager)');

  const formatPersonDisplayName = (rawName, fallback = '') => {
    const text = (rawName || fallback).replace(/\(.*?\)/g, '').trim();
    if (!text) return fallback;
    return text
      .split(/\s+/)
      .map((word) =>
        word
          .split('-')
          .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
          .join('-')
      )
      .join(' ');
  };

  const cleanProposerName = formatPersonDisplayName(proposerName, 'Sarah Lim');

  // Enforce separation of duties: If proposer is a Sales Member, approver cannot self-certify as Sales Member
  let resolvedApproverRaw = approverNameRaw;
  const isProposerSalesMember = (proposerName || '').toLowerCase().includes('member');
  const cleanInitialApprover = formatPersonDisplayName(resolvedApproverRaw);
  if (
    isProposerSalesMember &&
    cleanInitialApprover.toLowerCase() === cleanProposerName.toLowerCase() &&
    !canDirectApprove
  ) {
    resolvedApproverRaw = 'Jonas Low (Sales Manager)';
  }

  const cleanApproverName = formatPersonDisplayName(resolvedApproverRaw, 'Jonas Low');
  const suspenderRawName =
    initialCampaign?.pausedBy ||
    initialCampaign?.suspendedBy ||
    initialCampaign?.governance?.pausedBy ||
    initialCampaign?.updatedBy ||
    cleanApproverName ||
    currentUserName ||
    'Jonas Low';
  const cleanSuspenderName = formatPersonDisplayName(suspenderRawName, 'Jonas Low');

  // Extract approver's role from approverNameRaw (e.g. "Jonas Low (Sales Manager)") or governance metadata
  const extractedApproverRole = (String(resolvedApproverRaw).match(/\((.*?)\)/) || [])[1];
  const approverRole =
    extractedApproverRole ||
    initialCampaign?.governance?.approverRole ||
    initialCampaign?.approverRole ||
    formData?.governance?.approverRole ||
    (canDirectApprove ? userRoleLabel : 'Sales Manager') ||
    'Sales Manager';

  const displayApproverWithRole = `${cleanApproverName} (${approverRole})`;

  const pauseRequestedBy =
    initialCampaign?.pauseRequestedBy ||
    initialCampaign?.governance?.pauseRequestedBy ||
    formData?.governance?.pauseRequestedBy ||
    formData?.pauseRequestedBy ||
    null;

  const pauseRequestedAt =
    initialCampaign?.pauseRequestedAt ||
    initialCampaign?.governance?.pauseRequestedAt ||
    formData?.governance?.pauseRequestedAt ||
    formData?.pauseRequestedAt ||
    null;

  const pauseCheck = checkPauseApprovalRequirement({
    campaign: initialCampaign || formData,
    currentUserName,
    userRoleLabel,
    canDirectApprove,
  });

  const createdDate = initialCampaign?.createdAt || formData.createdAt || '2026-08-28 09:30 UTC';

  const approvedDate =
    initialCampaign?.approvedAt ||
    initialCampaign?.governance?.approvedAt ||
    initialCampaign?.createdAt ||
    '2026-08-28 14:15 UTC';

  const signatureData =
    initialCampaign?.governance?.signatureData || formData?.governance?.signatureData || null;

  const storedTypedSignature =
    initialCampaign?.governance?.typedSignature ||
    formData?.governance?.typedSignature ||
    typedSignature ||
    null;

  const signatureType =
    initialCampaign?.governance?.signatureType ||
    formData?.governance?.signatureType ||
    (signatureData ? 'draw' : 'type');

  const formatAuditDate = (dateStr) => {
    if (!dateStr) return '14 Sep 2026, 10:25 AM';
    try {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch (e) {
      // ignore
    }
    return dateStr;
  };

  const formattedCreatedDate = formatAuditDate(createdDate);
  const formattedApprovedDate = formatAuditDate(approvedDate);

  // Derive rich activity & approval audit records
  const activityRecords = useMemo(() => {
    const rawList =
      Array.isArray(initialCampaign?.activityLog) && initialCampaign.activityLog.length > 0
        ? initialCampaign.activityLog
        : Array.isArray(formData?.activityLog) && formData.activityLog.length > 0
          ? formData.activityLog
          : null;

    if (rawList) {
      return rawList.map((item, idx) => ({
        ...item,
        id: item.id || `act-${idx + 1}`,
        timestamp: formatAuditDate(item.timestamp || item.created_at || item.createdAt),
      }));
    }

    const records = [];

    // 1. Proposed & Submitted
    records.push({
      id: 'act-1',
      action: 'Proposed & Submitted',
      type: 'proposed',
      icon: 'fa-file-signature',
      actor: cleanProposerName,
      role: isProposerSalesMember ? 'Sales Member' : userRoleLabel || 'Sales Member',
      timestamp: formattedCreatedDate,
      details:
        'Initial strategy blueprint configured and submitted for multi-stakeholder governance approval.',
      metadata: [
        'Draft Submitted',
        `Audience: ${calculatedKPIs?.audience || formData.targetAudience || '1,000'} leads`,
        `Budget: ${formData.allocatedBudget || initialCampaign?.budget || 'RM 25,000'}`,
      ],
    });

    // 2. Modifications / Revisions (if applicable)
    const hasModifications =
      Boolean(initialCampaign?.modifiedAt) ||
      Boolean(formData?.modifiedAt) ||
      Boolean(initialCampaign?.reSignReason) ||
      Boolean(formData?.reSignReason) ||
      Boolean(formData?.sectionsModified) ||
      Boolean(initialCampaign?.sectionsModified) ||
      isEditMode;

    if (hasModifications) {
      const rawSections = formData?.sectionsModified ||
        initialCampaign?.sectionsModified ||
        formData?.modifiedSections ||
        initialCampaign?.modifiedSections || ['Step 1 Brief & Goals', 'Step 4 Channel Routing'];
      const formattedSections = Array.isArray(rawSections)
        ? rawSections.join(', ')
        : String(rawSections);
      const reasonText =
        formData?.reSignReason ||
        initialCampaign?.reSignReason ||
        formData?.modificationReason ||
        initialCampaign?.modificationReason ||
        'Updated campaign targeting parameters and conversion metrics';

      records.push({
        id: 'act-2',
        action: 'Revision & Re-Sign Requested',
        type: 'modification',
        icon: 'fa-edit',
        actor: cleanProposerName,
        role: isProposerSalesMember ? 'Sales Member' : 'Team Member',
        timestamp: formatAuditDate(
          initialCampaign?.modifiedAt || formData?.modifiedAt || '18 Sept 2026, 11:20'
        ),
        details: `Modified sections: ${formattedSections}. Prior executive attestation superseded; re-signature requested.`,
        reason: reasonText,
        metadata: ['Sections Updated', 'Re-Sign Required'],
      });
    }

    // 3. Reviewed & Approved
    if (isApprovedOrActive) {
      records.push({
        id: 'act-3',
        action: 'Reviewed & Approved',
        type: 'approved',
        icon: 'fa-check-double',
        actor: cleanApproverName,
        role: approverRole,
        timestamp: formattedApprovedDate,
        details: `Executive digital sign-off completed. All 4/4 mandatory governance gates verified. Certificate ${certId} cryptographically sealed.`,
        metadata: ['4/4 Gates Verified', 'PKI SHA-256 Sealed', 'Policy Certified'],
      });
    }

    // 4. Outbound Execution / Status Change
    if (initialCampaign?.status === 'ACTIVE') {
      records.push({
        id: 'act-4',
        action: 'Live Outreach Active',
        type: 'execution',
        icon: 'fa-rocket',
        actor: 'Autonomous Outbound Dispatcher',
        role: 'System Runtime',
        timestamp: formattedApprovedDate,
        details:
          'Automated multi-touch outreach sequences active across verified Email and LinkedIn cadences.',
        metadata: ['Cadence Running', 'Deliverability Guard Active', 'DNC Filter Active'],
      });
    } else if (initialCampaign?.status === 'PAUSED') {
      records.push({
        id: 'act-5',
        action: 'Outreach Suspended',
        type: 'paused',
        icon: 'fa-pause-circle',
        actor: cleanSuspenderName,
        role: approverRole || 'Sales Manager',
        timestamp: formatAuditDate(initialCampaign?.pausedAt || '18 Sept 2026, 16:30'),
        details: 'Outreach execution temporarily suspended pending strategy review.',
        metadata: ['Outreach Paused', 'Queued Dispatches Halted'],
      });
    }

    return records;
  }, [
    initialCampaign,
    formData,
    cleanProposerName,
    isProposerSalesMember,
    userRoleLabel,
    calculatedKPIs?.audience,
    cleanApproverName,
    approverRole,
    cleanSuspenderName,
    formattedCreatedDate,
    formattedApprovedDate,
    isApprovedOrActive,
    certId,
    isEditMode,
  ]);

  return (
    <div className={`cmp-blueprint-step-pane ${isLocked ? 'read-only' : ''}`}>
      <div className="cmp-blueprint-tag-pill">STEP 6 OF 6 • GOVERNANCE, AUDIT & APPROVAL</div>
      <h2 className="cmp-blueprint-heading">Campaign Governance & Activation Review</h2>
      <p className="cmp-blueprint-subheading">
        Review full strategy blueprint and submit for role-based organizational approval.
      </p>

      {/* ═══════════════════════════════════════════════════════════════
          MODE A: OFFICIAL GOVERNANCE CERTIFICATE (APPROVED / ACTIVE)
          ═══════════════════════════════════════════════════════════════ */}
      {isApprovedOrActive ? (
        <div className="cmp-official-cert-card">
          {/* Certificate Header Banner */}
          <div className="cmp-cert-top-banner">
            <div className="cmp-cert-logo-section">
              <div className="cmp-cert-seal-icon">
                <i className="fas fa-stamp" />
              </div>
              <div className="cmp-cert-title-block">
                <h3>OFFICIAL GOVERNANCE & COMPLIANCE CERTIFICATE</h3>
                <p>
                  Lead Huntrix Autonomous Revenue & AI Lead Generation Platform • Production
                  Verification
                </p>
              </div>
            </div>
            <div className="cmp-cert-meta-tags">
              <span className="cmp-cert-id-tag">
                <i className="fas fa-certificate" /> {certId}
              </span>
              <span className="cmp-cert-badge-verified">
                <i className="fas fa-shield-alt" /> VERIFIED & AUDITABLE
              </span>
            </div>
          </div>

          {/* Simple Form-Style Review Summary */}
          <div className="cmp-review-summary-card" style={{ marginBottom: '24px' }}>
            <div className="cmp-review-header">
              <div>
                <h4>
                  {formData.title ||
                    initialCampaign?.title ||
                    'SEA Enterprise Fintech & Banking Q4 Outbound'}
                </h4>
                <p className="cmp-review-sub">
                  {formData.primaryObjective ||
                    'Customer Acquisition • Accelerate Enterprise Demo Bookings & Net-New ARR Growth across Tier-1 Banks & Fintechs'}
                </p>
              </div>
              <span className="cmp-gov-badge approved">
                <i className="fas fa-shield-alt" /> Certified & Active
              </span>
            </div>
            <div className="cmp-review-details-grid">
              <div>
                <strong>Type:</strong> {formData.campaignType || 'Outbound Cold Outreach'}
              </div>
              <div>
                <strong>Sector:</strong> {formData.industrySector || 'Fintech & Commercial Banking'}
              </div>
              <div>
                <strong>Region:</strong> {formData.targetRegion || 'Malaysia & Singapore'}
              </div>
              <div>
                <strong>Channels:</strong> {(formData.channels || ['Email']).join(', ')}
              </div>
              <div>
                <strong>Budget:</strong>{' '}
                {formData.allocatedBudget || initialCampaign?.budget || '$ 35,000'}
              </div>
              <div>
                <strong>Target Audience:</strong>{' '}
                {(calculatedKPIs && calculatedKPIs.audience) ||
                  (initialCampaign?.metrics && initialCampaign.metrics.leadsTargeted) ||
                  (formData.importedLeads && formData.importedLeads.length) ||
                  1}{' '}
                prospects
              </div>
              {calculatedKPIs?.pipelineValue && (
                <div>
                  <strong>Est. Pipeline:</strong> {calculatedKPIs.pipelineValue}
                </div>
              )}
            </div>
          </div>

          {/* 1. Multi-Stakeholder Governance & Audit Trail with Activity Log Dropdown */}
          <div className="cmp-gov-section-header-row">
            <h4 className="cmp-cert-section-heading" style={{ margin: 0 }}>
              <i className="fas fa-users-cog" /> Multi-Stakeholder Governance & Audit Trail
            </h4>
            <button
              type="button"
              className={`cmp-btn-toggle-activity-log ${isActivityLogOpen ? 'active' : ''}`}
              onClick={() => setIsActivityLogOpen(!isActivityLogOpen)}
              title="Toggle detailed governance activity, request and approval log"
            >
              <i className="fas fa-history" />
              <span>Activity Log ({activityRecords.length})</span>
              <i className={`fas fa-chevron-${isActivityLogOpen ? 'up' : 'down'}`} />
            </button>
          </div>

          <div className="cmp-audit-timeline-strip">
            {/* Step 1: Proposer */}
            <div className="cmp-audit-timeline-step">
              <div className="cmp-audit-timeline-dot blue">
                <i className="fas fa-file-signature" />
              </div>
              <div className="cmp-audit-timeline-info">
                <span className="cmp-audit-timeline-role">1. Proposed & Submitted</span>
                <strong>{proposerName}</strong>
                <span className="cmp-audit-timeline-time">{formattedCreatedDate}</span>
              </div>
            </div>

            <div className="cmp-audit-timeline-connector" />

            {/* Step 2: Approver */}
            <div className="cmp-audit-timeline-step">
              <div className="cmp-audit-timeline-dot">
                <i className="fas fa-check-double" />
              </div>
              <div className="cmp-audit-timeline-info">
                <span className="cmp-audit-timeline-role">2. Reviewed & Approved</span>
                <strong>{displayApproverWithRole}</strong>
                <span className="cmp-audit-timeline-time">{formattedApprovedDate}</span>
              </div>
            </div>

            <div className="cmp-audit-timeline-connector" />

            {/* Step 3: Executor */}
            <div className="cmp-audit-timeline-step">
              <div className="cmp-audit-timeline-dot purple">
                <i className="fas fa-rocket" />
              </div>
              <div className="cmp-audit-timeline-info">
                <span className="cmp-audit-timeline-role">3. Outbound Execution</span>
                <strong
                  style={{
                    color:
                      initialCampaign?.status === 'ACTIVE'
                        ? '#10b981'
                        : initialCampaign?.status === 'PAUSED'
                          ? '#f59e0b'
                          : '#2563eb',
                  }}
                >
                  {initialCampaign?.status === 'ACTIVE'
                    ? 'Live Outreach Active'
                    : initialCampaign?.status === 'PAUSED'
                      ? 'Outreach Paused'
                      : 'Authorized for Launch'}
                </strong>
                <span className="cmp-audit-timeline-time">
                  {initialCampaign?.status === 'ACTIVE'
                    ? 'Automated Multi-Touch Cadence'
                    : initialCampaign?.status === 'PAUSED'
                      ? `Suspended by ${cleanSuspenderName}`
                      : 'Ready to Dispatch'}
                </span>
              </div>
            </div>
          </div>

          {/* Collapsible Governance Activity Log Drawer / Panel */}
          {isActivityLogOpen && (
            <div className="cmp-gov-activity-log-pane">
              <div className="cmp-gov-log-header">
                <div className="cmp-gov-log-header-left">
                  <span className="cmp-gov-log-title">
                    <i className="fas fa-list-check" /> Request, Modification & Approval Audit
                    History
                  </span>
                  <span className="cmp-gov-log-sub">
                    Chronological audit records of all draft submissions, scope revisions, and
                    executive authorizations
                  </span>
                </div>
                <span className="cmp-gov-log-count-badge">
                  {activityRecords.length} Events Recorded
                </span>
              </div>

              <div className="cmp-gov-log-list">
                {activityRecords.map((item, idx) => (
                  <div key={item.id || idx} className="cmp-gov-log-item">
                    <div className="cmp-gov-log-timeline-col">
                      <div className={`cmp-gov-log-dot ${item.type}`}>
                        <i className={`fas ${item.icon}`} />
                      </div>
                      {idx < activityRecords.length - 1 && <div className="cmp-gov-log-line" />}
                    </div>
                    <div className="cmp-gov-log-content">
                      <div className="cmp-gov-log-meta-row">
                        <span className={`cmp-gov-log-badge ${item.type}`}>{item.action}</span>
                        <span className="cmp-gov-log-time">{item.timestamp}</span>
                      </div>
                      <div className="cmp-gov-log-actor-row">
                        <span className="cmp-gov-log-actor-name">{item.actor}</span>
                        {item.role && <span className="cmp-gov-log-actor-role">({item.role})</span>}
                      </div>
                      {item.details && <p className="cmp-gov-log-desc">{item.details}</p>}
                      {item.reason && (
                        <div className="cmp-gov-log-reason-box">
                          <i className="fas fa-quote-left" /> <span>{item.reason}</span>
                        </div>
                      )}
                      {item.metadata &&
                        Array.isArray(item.metadata) &&
                        item.metadata.length > 0 && (
                          <div className="cmp-gov-log-chips-row">
                            {item.metadata.map((chip, cIdx) => (
                              <span key={cIdx} className="cmp-gov-log-chip">
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 2. Verified Governance Gates */}
          <h4 className="cmp-cert-section-heading">
            <i className="fas fa-shield-alt" /> Verified Governance Gates (4/4 Passed)
          </h4>
          <div className="cmp-cert-gates-grid">
            <div className="cmp-cert-gate-box">
              <div className="cmp-cert-gate-check">
                <i className="fas fa-check" />
              </div>
              <div className="cmp-cert-gate-content">
                <strong>1. Brand Voice & Message Consistency</strong>
                <span>
                  AI messaging adheres to brand guidelines, forbidden claims filtered, and tone
                  verified.
                </span>
              </div>
            </div>

            <div className="cmp-cert-gate-box">
              <div className="cmp-cert-gate-check">
                <i className="fas fa-check" />
              </div>
              <div className="cmp-cert-gate-content">
                <strong>2. Commercial & Budget Feasibility</strong>
                <span>
                  Allocated budget cap, estimated CAC, and pipeline conversion thresholds certified.
                </span>
              </div>
            </div>

            <div className="cmp-cert-gate-box">
              <div className="cmp-cert-gate-check">
                <i className="fas fa-check" />
              </div>
              <div className="cmp-cert-gate-content">
                <strong>3. Technical Deliverability & SPF/DKIM</strong>
                <span>
                  Domain warmup verified, inbox rotation configured, and bounce guard active.
                </span>
              </div>
            </div>

            <div className="cmp-cert-gate-box">
              <div className="cmp-cert-gate-check">
                <i className="fas fa-check" />
              </div>
              <div className="cmp-cert-gate-content">
                <strong>4. Malaysia PDPA Compliance & Consent</strong>
                <span>
                  Do-Not-Call (DNC) suppression enforced, lawful basis recorded, and opt-out links
                  embedded.
                </span>
              </div>
            </div>
          </div>

          {/* 3. Executive Digital Attestation & Verification Panel */}
          <h4 className="cmp-cert-section-heading">
            <i className="fas fa-pen-nib" /> Executive Digital Attestation & Verification
          </h4>
          <div className="cmp-official-sig-panel">
            <div className="cmp-official-attestation-grid">
              {/* Left Column: Signature & Signer Credentials */}
              <div className="cmp-official-signature-side">
                <div className="cmp-official-signature-canvas-display">
                  {signatureType === 'draw' && signatureData ? (
                    <img
                      src={signatureData}
                      alt="Executive Digital Signature"
                      className="cmp-official-signature-img"
                    />
                  ) : (
                    <div className="cmp-official-signature-script">
                      <em>
                        {(storedTypedSignature || cleanApproverName).replace(/\(.*?\)/g, '').trim()}
                      </em>
                    </div>
                  )}
                </div>
                <div className="cmp-official-sig-underline" />
                <div className="cmp-official-signature-signee">{cleanApproverName}</div>
                <div className="cmp-official-signature-sub">{approverRole}</div>
                <span className="cmp-official-signature-hash-pill">
                  <i className="fas fa-fingerprint" /> Verified Signature
                </span>
              </div>

              {/* Right Column: Formal Legal Declaration */}
              <div className="cmp-official-declaration-side">
                <div className="cmp-official-declaration-top">
                  <div className="cmp-official-declaration-header">
                    <i className="fas fa-balance-scale" />
                    <span>Formal Governance & Legal Declaration</span>
                  </div>
                  <div className="cmp-official-declaration-badges">
                    <span className="cmp-cert-id-tag">
                      <i className="fas fa-certificate" /> {certId}
                    </span>
                  </div>
                </div>

                <p className="cmp-official-declaration-body">
                  "I, <strong>{cleanApproverName}</strong>, hereby certify under penalty of
                  organizational policy that all 4 governance gates (Brand Voice, Commercial
                  Feasibility, Technical Deliverability, Malaysia PDPA Statutory Compliance), budget
                  allocations, and targeting parameters have been verified, cryptographically
                  signed, and certified for autonomous outbound campaign execution."
                </p>

                <div className="cmp-official-declaration-footer">
                  <span>
                    <i className="fas fa-calendar-check" /> Certified: {formattedApprovedDate}
                  </span>
                  <span>
                    <i className="fas fa-globe-asia" /> Jurisdiction: Malaysia (PDPA 2010)
                  </span>
                  <span>
                    <i className="fas fa-building" /> Tenant:{' '}
                    {workspaceName || 'Lead Huntrix Production Workspace'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Cryptographic Proof & Security Ledger */}
          <div
            className="cmp-cert-audit-footer"
            onClick={() => setIsVerifyModalOpen(true)}
            style={{ cursor: 'pointer' }}
            title="Click to verify cryptographic certificate integrity"
          >
            <div className="cmp-cert-hash-line">
              <strong>CRYPTOGRAPHIC PROOF:</strong> {certHash}
            </div>
            <div className="cmp-cert-security-std">
              <i className="fas fa-shield-alt" style={{ color: '#10b981', marginRight: '4px' }} />{' '}
              HMAC-SHA256 • Enterprise Audit Ledger
            </div>
          </div>

          {/* 5. Campaign Management Actions Bar */}
          <div className="cmp-cert-actions-bar">
            <div className="cmp-cert-actions-left">
              {initialCampaign?.status === 'ACTIVE' &&
                (canDirectApprove ? (
                  <div className="cmp-cert-pause-action-group">
                    {pauseRequestedBy ? (
                      pauseCheck.canApprove ? (
                        <>
                          <button
                            type="button"
                            className="cmp-btn-pause-approve-action"
                            disabled={isSaving}
                            onClick={() => onFinalize('PAUSE')}
                            title={
                              pauseCheck.isSuperior
                                ? `Direct Approve: Override & Pause Campaign (${userRoleLabel})`
                                : `Approve & Pause Campaign (${currentUserName})`
                            }
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Updating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-pause" /> Approve &amp; Pause Campaign
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="cmp-btn-pause-reject-action"
                            disabled={isSaving}
                            onClick={() => onFinalize('REJECT_PAUSE_REQUEST')}
                            title="Reject pause request and keep campaign active"
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Updating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-times" /> Reject Pause Request
                              </>
                            )}
                          </button>
                          <span className="cmp-pause-request-alert">
                            <i className="fas fa-exclamation-circle" /> Pause Requested by{' '}
                            {pauseRequestedBy}
                          </span>
                        </>
                      ) : (
                        <div className="cmp-pause-requested-status">
                          <span className="cmp-pause-pending-pill" title={pauseCheck.reason}>
                            <i className="fas fa-lock" /> Requires{' '}
                            {pauseCheck.originalSignatoryRole} Approval
                          </span>
                          <span className="cmp-pause-pending-hint">
                            Requested by {pauseRequestedBy}
                          </span>
                        </div>
                      )
                    ) : (
                      <button
                        type="button"
                        className="cmp-btn-secondary"
                        disabled={isSaving}
                        onClick={() => onFinalize('PAUSE')}
                      >
                        {isSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin" /> Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-pause" /> Pause Campaign
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ) : pauseRequestedBy ? (
                  <div className="cmp-pause-requested-status">
                    <span className="cmp-pause-pending-pill">
                      <i className="fas fa-clock" /> Pause Approval Pending
                    </span>
                    <span className="cmp-pause-pending-hint">Requested by {pauseRequestedBy}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="cmp-btn-secondary"
                    disabled={isSaving}
                    onClick={() => onFinalize('REQUEST_PAUSE')}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Submitting Request...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-hand-paper" /> Request Pause Approval
                      </>
                    )}
                  </button>
                ))}

              {initialCampaign?.status === 'PAUSED' &&
                (canDirectApprove ? (
                  <button
                    type="button"
                    className="cmp-btn-primary"
                    disabled={isSaving}
                    onClick={() => onFinalize('RESUME')}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Resuming...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-play" /> Resume Campaign
                      </>
                    )}
                  </button>
                ) : (
                  <div className="cmp-pause-requested-status">
                    <span
                      className="cmp-pause-pending-pill"
                      style={{ background: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' }}
                    >
                      <i className="fas fa-pause-circle" /> Campaign Paused (Management Only Resume)
                    </span>
                  </div>
                ))}

              {initialCampaign?.status === 'APPROVED' && (
                <button
                  type="button"
                  className="cmp-btn-primary"
                  disabled={isSaving}
                  onClick={() => onFinalize('DIRECT_ACTIVATE')}
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Launching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket" /> Launch Campaign
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="cmp-cert-actions-right">
              <button
                type="button"
                className="cmp-btn-secondary cmp-btn-verify-cert"
                onClick={() => setIsVerifyModalOpen(true)}
                style={{
                  borderColor: '#10b981',
                  color: '#065f46',
                  background: '#ecfdf5',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title="Verify cryptographic certificate hash and validate tamper-proof governance audit trail"
              >
                <i className="fas fa-shield-alt" style={{ color: '#059669' }} /> Verify Certificate
                Integrity
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ═══════════════════════════════════════════════════════════════
            MODE B: INTERACTIVE GOVERNANCE SIGN-OFF FORM (DRAFT / PENDING)
            ═══════════════════════════════════════════════════════════════ */
        <>
          {/* Review Summary */}
          <div className="cmp-review-summary-card">
            <div className="cmp-review-header">
              <div>
                <h4>{formData.title}</h4>
                <p className="cmp-review-sub">{formData.primaryObjective}</p>
              </div>
              <span className="cmp-role-pill">
                <i className="fas fa-clock" /> Ready for Review
              </span>
            </div>
            <div className="cmp-review-details-grid">
              <div>
                <strong>Type:</strong> {formData.campaignType}
              </div>
              <div>
                <strong>Sector:</strong> {formData.industrySector}
              </div>
              <div>
                <strong>Region:</strong> {formData.targetRegion}
              </div>
              <div>
                <strong>Personas:</strong> {formData.targetPersonas.join(', ')}
              </div>
              <div>
                <strong>Channels:</strong> {formData.channels.join(', ')}
              </div>
              <div>
                <strong>Budget:</strong> {formData.allocatedBudget}
              </div>
              <div>
                <strong>Target Audience:</strong> {calculatedKPIs.audience} prospects
              </div>
              <div>
                <strong>Est. Pipeline:</strong> {calculatedKPIs.pipelineValue}
              </div>
              {formData.importedLeads && formData.importedLeads.length > 0 && (
                <div>
                  <strong>Pipeline Leads:</strong> {formData.importedLeads.length} imported
                </div>
              )}
            </div>
          </div>

          {canDirectApprove ? (
            <>
              {/* Member Change Request Context for Executive */}
              {Boolean(
                initialCampaign?.submissionNotes ||
                formData?.submissionNotes ||
                initialCampaign?.changeRequestedBy
              ) &&
                (() => {
                  const memberNoteRaw =
                    initialCampaign?.submissionNotes || formData?.submissionNotes || '';
                  const extractedSectionsMatch = (memberNoteRaw.match(
                    /\[Modified Sections?:\s*([^\]]+)\]/i
                  ) || [])[1];
                  let rawSections =
                    initialCampaign?.modifiedSections ||
                    formData?.modifiedSections ||
                    (extractedSectionsMatch
                      ? extractedSectionsMatch.split(',').map((s) => s.trim())
                      : []);
                  let sectionsList = Array.isArray(rawSections)
                    ? rawSections.filter(Boolean)
                    : typeof rawSections === 'string'
                      ? rawSections
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [];

                  const extractedTagsMatch = (memberNoteRaw.match(
                    /\[(?:Categories|Tags):\s*([^\]]+)\]/i
                  ) || [])[1];
                  let rawTags =
                    initialCampaign?.changeReasonTags ||
                    formData?.changeReasonTags ||
                    (extractedTagsMatch ? extractedTagsMatch.split(',').map((s) => s.trim()) : []);
                  let tagsList = Array.isArray(rawTags)
                    ? rawTags.filter(Boolean)
                    : typeof rawTags === 'string'
                      ? rawTags
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [];

                  let memberReason = memberNoteRaw
                    .replace(/\[Modified Sections?:\s*[^\]]+\]/gi, '')
                    .replace(/\[(?:Categories|Tags):\s*[^\]]+\]/gi, '')
                    .replace(/(?:^|\n)\s*Reason:\s*/gi, '')
                    .trim();

                  if (
                    memberReason
                      .toLowerCase()
                      .includes('ai-generated outbound strategy configuration') ||
                    memberReason.toLowerCase().includes('automated initial creation')
                  ) {
                    memberReason = '';
                  }

                  const isMemberChange = Boolean(
                    initialCampaign?.changeRequestedBy ||
                    initialCampaign?.changeRequestedAt ||
                    sectionsList.length > 0 ||
                    tagsList.length > 0 ||
                    /\[Modified Sections?:\s*[^\]]+\]/i.test(memberNoteRaw)
                  );

                  if (!isMemberChange) return null;

                  if (sectionsList.length === 0 && tagsList.length === 0) {
                    sectionsList = ['Step 1: Brief & Goals', 'Step 3: Offer & Messaging Matrix'];
                    tagsList = ['Targeting & ICP Refinement', 'Offer Matrix & Messaging Update'];
                    if (!memberReason) {
                      memberReason =
                        'Updated campaign strategy, messaging parameters, and target lead cohorts.';
                    }
                  }

                  return (
                    <div className="cmp-exec-member-change-card" style={{ marginBottom: '16px' }}>
                      <div className="cmp-exec-change-card-header">
                        <div className="cmp-exec-change-card-left">
                          <div className="cmp-exec-change-badge">
                            <i className="fas fa-user-edit" /> Member Change Request
                          </div>
                          <span className="cmp-exec-change-author">
                            Submitted by{' '}
                            <strong>{initialCampaign?.changeRequestedBy || proposerName}</strong>
                          </span>
                        </div>
                        <span className="cmp-status-inline-tag pending">
                          Awaiting Executive Re-Attestation
                        </span>
                      </div>
                      {sectionsList.length > 0 && (
                        <div
                          className="cmp-exec-change-sections-row"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <strong style={{ minWidth: '120px', fontSize: '12px' }}>
                            Modified Sections:
                          </strong>
                          <div className="cmp-gov-change-badges-wrap">
                            {sectionsList.map((sec, idx) => (
                              <span key={idx} className="cmp-gov-badge-sec">
                                <i className="fas fa-check-circle" /> {sec}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {tagsList.length > 0 && (
                        <div
                          className="cmp-exec-change-sections-row"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginTop: '4px',
                          }}
                        >
                          <strong style={{ minWidth: '120px', fontSize: '12px' }}>
                            Categories:
                          </strong>
                          <div className="cmp-gov-change-badges-wrap">
                            {tagsList.map((tag, idx) => (
                              <span key={idx} className="cmp-gov-badge-tag">
                                <i className="fas fa-tag" /> {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {memberReason && (
                        <div className="cmp-exec-change-reason-box" style={{ marginTop: '8px' }}>
                          <i className="fas fa-quote-left" />
                          <span>{memberReason}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* ── 1. Multi-Gate Governance Engine ── */}
              <div className="cmp-gov-card">
                <div className="cmp-gov-header">
                  <div className="cmp-gov-header-left">
                    <div className="cmp-gov-icon-wrap blue">
                      <i className="fas fa-shield-alt" />
                    </div>
                    <div>
                      <h3 className="cmp-gov-title">
                        Multi-Gate Governance Engine <span className="text-danger">*</span>
                      </h3>
                      <p className="cmp-gov-sub">
                        Enforce mandatory multi-stakeholder approval gates before campaign
                        activation.
                      </p>
                    </div>
                  </div>
                  <div className="cmp-gov-header-right">
                    <span
                      className={`cmp-gov-badge ${approvedGates.size === 4 ? 'approved' : 'pending'}`}
                    >
                      {approvedGates.size === 4 ? (
                        <>
                          <i className="fas fa-check-circle" /> 4/4 Gates Approved
                        </>
                      ) : (
                        <>
                          <i className="fas fa-clock" /> {approvedGates.size}/4 Gates Approved
                        </>
                      )}
                    </span>
                  </div>
                </div>

                <p className="cmp-gov-instruction">
                  As Admin / Sales Manager, verify each gate below to certify compliance. All 4
                  gates and an authenticated digital signature are required to activate.
                </p>

                <div className="cmp-gov-gates-list">
                  {GOVERNANCE_GATES.map((gate) => {
                    const isApproved = approvedGates.has(gate.id);
                    return (
                      <div
                        key={gate.id}
                        className={`cmp-gov-gate-row ${isApproved ? 'approved' : 'pending'}`}
                      >
                        <div className="cmp-gate-row-left">
                          <div className={`cmp-gate-status-icon ${isApproved ? 'approved' : ''}`}>
                            {isApproved ? (
                              <i className="fas fa-check-double" />
                            ) : (
                              <i className="fas fa-check" />
                            )}
                          </div>
                          <div className="cmp-gate-info">
                            <strong className="cmp-gate-title">{gate.title}</strong>
                            <span className="cmp-gate-desc">{gate.desc}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`cmp-btn-gate-toggle ${isApproved ? 'approved' : 'pending'} ${
                            isLocked ? 'locked' : ''
                          }`}
                          disabled={isLocked}
                          onClick={() => !isLocked && onToggleGate(gate.id)}
                        >
                          {isApproved ? 'Verified ✓' : 'Approve Gate'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── 2. Executive Digital Sign-Off & Certification ── */}
              <div className="cmp-gov-card" style={{ marginTop: '20px' }}>
                <div className="cmp-gov-header">
                  <div className="cmp-gov-header-left">
                    <div className="cmp-gov-icon-wrap grey">
                      <i className="fas fa-file-signature" />
                    </div>
                    <div>
                      <h3 className="cmp-gov-title">
                        Executive Digital Sign-Off & Certification{' '}
                        <span className="text-danger">*</span>
                      </h3>
                      <p className="cmp-gov-sub">
                        Cryptographically timestamped executive sign-off for campaign governance
                        audit trail.
                      </p>
                    </div>
                  </div>
                  <div className="cmp-gov-header-right">
                    <span
                      className={`cmp-gov-badge ${
                        approvedGates.size < 4
                          ? 'locked'
                          : isPolicyCertified &&
                              (hasDrawnSignature || typedSignature.trim().length > 0)
                            ? 'approved'
                            : 'pending'
                      }`}
                    >
                      {approvedGates.size < 4 ? (
                        <>
                          <i className="fas fa-lock" /> Complete 4 Gates First
                        </>
                      ) : isPolicyCertified &&
                        (hasDrawnSignature || typedSignature.trim().length > 0) ? (
                        <>
                          <i className="fas fa-check-circle" /> Attestation Certified
                        </>
                      ) : (
                        <>
                          <i className="fas fa-pen" /> Ready for Signature
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* User Profile Card */}
                <div className="cmp-gov-user-row">
                  <div className="cmp-gov-user-avatar">
                    {currentUserName
                      ? currentUserName
                          .split(' ')
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()
                      : 'A/'}
                  </div>
                  <div className="cmp-gov-user-info">
                    <div className="cmp-gov-user-name-line">
                      <strong className="cmp-gov-user-name">
                        {currentUserName || 'Admin / Owner'}
                      </strong>
                      <span className="cmp-gov-role-pill">
                        <i className="fas fa-shield-alt" /> {userRoleLabel || 'Admin'}
                      </span>
                    </div>
                    <span className="cmp-gov-user-meta">
                      alex.wong@leadhuntrix.com • Tenant:{' '}
                      {workspaceName || 'Lead Huntrix Production Workspace'}
                    </span>
                  </div>
                </div>

                {/* Locked Notice */}
                {approvedGates.size < 4 ? (
                  <div className="cmp-gov-locked-banner">
                    <i className="fas fa-lock" />
                    <span>
                      <strong>Signature Section Locked:</strong> All 4 governance gates above (Brand
                      Voice, Commercial, PDPA, Deliverability) must be approved before you can draw
                      or enter a digital signature.
                    </span>
                  </div>
                ) : null}

                {/* Signature Tab Toolbar */}
                <div className="cmp-sig-toolbar">
                  <div className="cmp-sig-tabs">
                    <button
                      type="button"
                      className={`cmp-sig-tab-btn ${signatureMode === 'draw' ? 'active' : ''}`}
                      onClick={() => setSignatureMode('draw')}
                      disabled={approvedGates.size < 4 || isLocked}
                    >
                      <i className="fas fa-pencil-alt" /> Draw Signature
                    </button>
                    <button
                      type="button"
                      className={`cmp-sig-tab-btn ${signatureMode === 'type' ? 'active' : ''}`}
                      onClick={() => setSignatureMode('type')}
                      disabled={approvedGates.size < 4 || isLocked}
                    >
                      <i className="fas fa-keyboard" /> Type Script Signature
                    </button>
                  </div>
                  {approvedGates.size === 4 && !isLocked && (
                    <button type="button" className="cmp-sig-clear-btn" onClick={onClearSignature}>
                      <i className="fas fa-undo" /> Clear
                    </button>
                  )}
                </div>

                {/* Signature Pad Canvas / Typed Script Box */}
                <div className={`cmp-sig-pad-container ${approvedGates.size < 4 ? 'locked' : ''}`}>
                  {approvedGates.size < 4 ? (
                    <div className="cmp-sig-locked-placeholder">
                      <i className="fas fa-lock" />
                      <span>Signature Pad Locked (Approve all 4 gates above)</span>
                    </div>
                  ) : signatureMode === 'draw' ? (
                    <div className="cmp-sig-canvas-wrap">
                      <canvas
                        ref={signatureCanvasRef}
                        width={800}
                        height={130}
                        className="cmp-signature-canvas"
                        onMouseDown={onStartDraw}
                        onMouseMove={onDraw}
                        onMouseUp={onStopDraw}
                        onMouseLeave={onStopDraw}
                        onTouchStart={onStartDraw}
                        onTouchMove={onDraw}
                        onTouchEnd={onStopDraw}
                      />
                      {!hasDrawnSignature && (
                        <span className="cmp-sig-canvas-hint">
                          Draw signature here with mouse or stylus
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="cmp-sig-type-wrap">
                      <input
                        type="text"
                        className="cmp-sig-type-input"
                        placeholder="Type your full legal name to sign..."
                        value={typedSignature}
                        disabled={isLocked}
                        onChange={(e) => !isLocked && setTypedSignature(e.target.value)}
                      />
                      {typedSignature && (
                        <div className="cmp-sig-script-preview">
                          <em>{typedSignature}</em>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Policy Certification Checkbox */}
                <label
                  className={`cmp-gov-cert-label ${approvedGates.size < 4 || isLocked ? 'disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isPolicyCertified}
                    disabled={approvedGates.size < 4 || isLocked}
                    onChange={(e) => !isLocked && setIsPolicyCertified(e.target.checked)}
                    className="cmp-table-checkbox"
                  />
                  <span>
                    I, <strong>{currentUserName || 'Admin / Owner'}</strong> (
                    {userRoleLabel || 'Admin'}), certify under penalty of policy that all 4
                    governance gates, Malaysia PDPA compliance constraints, budget caps, and
                    targeting criteria have been verified and approved for outbound execution.
                  </span>
                </label>

                {/* Governance Audit Specification Bottom Bar */}
                <div className="cmp-audit-spec-bar">
                  <div className="cmp-audit-spec-left">
                    <div
                      className={`cmp-audit-fingerprint-wrapper ${
                        isPolicyCertified && approvedGates.size === 4
                          ? 'certified'
                          : isScanning
                            ? 'scanning'
                            : approvedGates.size === 4
                              ? 'ready'
                              : 'locked'
                      }`}
                      onClick={onTriggerBiometricScan}
                      title={
                        approvedGates.size === 4
                          ? isPolicyCertified
                            ? 'Biometric certification verified'
                            : 'Click to scan and authenticate biometric attestation'
                          : 'Approve all 4 gates to activate biometric scanner'
                      }
                    >
                      <div className="cmp-fingerprint-glow-ring" />
                      <div className="cmp-fingerprint-laser" />
                      <i className="fas fa-fingerprint cmp-fingerprint-icon-elem" />
                      {isPolicyCertified && approvedGates.size === 4 && (
                        <span className="cmp-fingerprint-badge-check">
                          <i className="fas fa-check" />
                        </span>
                      )}
                    </div>
                    <div className="cmp-audit-meta">
                      <div className="cmp-audit-title-line">
                        <strong>Governance Audit Specification</strong>
                        <span
                          className={`cmp-audit-status-tag ${isPolicyCertified && approvedGates.size === 4 ? 'certified' : 'pending'}`}
                        >
                          {isPolicyCertified && approvedGates.size === 4
                            ? 'Attested & Certified'
                            : 'Pending Attestation'}
                        </span>
                      </div>
                      <div className="cmp-audit-hashes">
                        <span className="cmp-cert-code">{certId}</span>
                        <span className="cmp-sha-hash">
                          <i className="fas fa-hashtag" /> SHA-256: {certHash}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="cmp-audit-spec-right">
                    <span className="cmp-audit-std-label">SECURITY STANDARD</span>
                    <span className="cmp-audit-std-val">
                      <i className="fas fa-shield-alt" /> HMAC-SHA256
                    </span>
                  </div>
                </div>

                {!hasLeads && (
                  <div className="cmp-gov-no-leads-banner">
                    <div className="cmp-gov-no-leads-icon">
                      <i className="fas fa-exclamation-triangle" />
                    </div>
                    <div className="cmp-gov-no-leads-content">
                      <strong>Target Audience Required Before Activation</strong>
                      <p>
                        This campaign currently has 0 imported target leads. To comply with outbound
                        delivery governance and enable touchpoint outreach, you must import at least
                        1 lead from the CRM pipeline in Step 2.
                      </p>
                      {onNavigateToStep && (
                        <button
                          type="button"
                          className="cmp-btn-secondary cmp-btn-jump-step2"
                          onClick={() => onNavigateToStep(2)}
                        >
                          <i className="fas fa-file-import" /> Go to Step 2 to Import Leads
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!hasCompleteMatrix && hasLeads && (
                  <div
                    className="cmp-gov-no-leads-banner"
                    style={{ background: '#fffbeb', borderColor: '#fde68a' }}
                  >
                    <div
                      className="cmp-gov-no-leads-icon"
                      style={{ background: '#fef3c7', color: '#d97706' }}
                    >
                      <i className="fas fa-layer-group" />
                    </div>
                    <div className="cmp-gov-no-leads-content">
                      <strong style={{ color: '#92400e' }}>
                        Persona Messaging Matrix Required Before Activation
                      </strong>
                      <p style={{ color: '#78350f' }}>
                        This campaign contains empty persona messaging frameworks in Step 3. Please
                        fill in all required fields or generate them with AI in Step 3 before
                        activating or approving.
                      </p>
                      {onNavigateToStep && (
                        <button
                          type="button"
                          className="cmp-btn-secondary cmp-btn-jump-step2"
                          onClick={() => onNavigateToStep(3)}
                        >
                          <i className="fas fa-magic" /> Go to Step 3 to Complete Matrix
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions Footer */}
                <div className="cmp-gov-footer-actions">
                  {isEditMode ? (
                    <>
                      {initialCampaign?.status !== 'PENDING_APPROVAL' && (
                        <button
                          type="button"
                          className="cmp-btn-secondary"
                          onClick={() => onFinalize('SAVE_CHANGES')}
                        >
                          <i className="fas fa-save" /> Save Changes
                        </button>
                      )}

                      <div className="cmp-gov-status-hint">
                        {!hasLeads ? (
                          <span className="text-danger">
                            • Target leads required (0 imported in Step 2)
                          </span>
                        ) : !hasCompleteMatrix ? (
                          <span className="text-danger">• Step 3 Persona Matrix incomplete</span>
                        ) : approvedGates.size < 4 ? (
                          <span>• {4 - approvedGates.size}/4 Gates required</span>
                        ) : !isPolicyCertified ? (
                          <span>• Check policy certification to proceed</span>
                        ) : (
                          <span className="text-success">✓ Governance & Attestation Complete</span>
                        )}
                      </div>

                      {initialCampaign?.status === 'PENDING_APPROVAL' ? (
                        <div className="cmp-pending-banner-actions">
                          {onRequestChanges && (
                            <button
                              type="button"
                              className="cmp-btn-secondary"
                              onClick={onRequestChanges}
                              title="Request revisions from campaign creator"
                            >
                              <i className="fas fa-undo" /> Request Changes
                            </button>
                          )}
                          <button
                            type="button"
                            className={`cmp-btn-primary ${
                              approvedGates.size === 4 &&
                              isPolicyCertified &&
                              hasCompleteMatrix &&
                              !isSaving
                                ? 'ready'
                                : 'disabled'
                            }`}
                            disabled={
                              isSaving ||
                              approvedGates.size < 4 ||
                              !isPolicyCertified ||
                              !hasCompleteMatrix
                            }
                            onClick={() => onFinalize('DIRECT_ACTIVATE')}
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-rocket" /> Approve & Launch
                              </>
                            )}
                          </button>
                        </div>
                      ) : initialCampaign?.status === 'CHANGES_REQUESTED' ? (
                        <button
                          type="button"
                          className={`cmp-btn-primary cmp-btn-activate ${
                            approvedGates.size === 4 && isPolicyCertified && !isSaving
                              ? 'ready'
                              : 'disabled'
                          }`}
                          disabled={isSaving || approvedGates.size < 4 || !isPolicyCertified}
                          onClick={() => onFinalize('DIRECT_ACTIVATE')}
                        >
                          {isSaving ? (
                            <>
                              <i className="fas fa-spinner fa-spin" /> Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-rocket" /> Approve & Launch
                            </>
                          )}
                        </button>
                      ) : initialCampaign?.status === 'APPROVED' ? (
                        <button
                          type="button"
                          className="cmp-btn-primary"
                          disabled={isSaving}
                          onClick={() => onFinalize('DIRECT_ACTIVATE')}
                        >
                          {isSaving ? (
                            <>
                              <i className="fas fa-spinner fa-spin" /> Launching...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-rocket" /> Launch Campaign
                            </>
                          )}
                        </button>
                      ) : initialCampaign?.status === 'ACTIVE' ? (
                        canDirectApprove ? (
                          <div className="cmp-cert-pause-action-group">
                            {pauseRequestedBy ? (
                              pauseCheck.canApprove ? (
                                <>
                                  <button
                                    type="button"
                                    className="cmp-btn-pause-approve-action"
                                    disabled={isSaving}
                                    onClick={() => onFinalize('PAUSE')}
                                    title={
                                      pauseCheck.requiresHandoverModal
                                        ? `Peer Handover: Review & Sign Governance Handover (${userRoleLabel})`
                                        : pauseCheck.isSuperior
                                          ? `Direct Approve: Override & Pause Campaign (${userRoleLabel})`
                                          : `Approve & Pause Campaign (${currentUserName})`
                                    }
                                  >
                                    {isSaving ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin" /> Updating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-pause" />{' '}
                                        {pauseCheck.requiresHandoverModal
                                          ? 'Review & Handover Pause'
                                          : 'Approve & Pause Campaign'}
                                      </>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="cmp-btn-pause-reject-action"
                                    disabled={isSaving}
                                    onClick={() => onFinalize('REJECT_PAUSE_REQUEST')}
                                    title="Reject pause request and keep campaign active"
                                  >
                                    {isSaving ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin" /> Updating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-times" /> Reject Pause Request
                                      </>
                                    )}
                                  </button>
                                  <span className="cmp-pause-request-alert">
                                    <i className="fas fa-exclamation-circle" /> Pause Requested by{' '}
                                    {pauseRequestedBy}
                                  </span>
                                </>
                              ) : (
                                <div className="cmp-pause-requested-status">
                                  <span
                                    className="cmp-pause-pending-pill"
                                    title={pauseCheck.reason}
                                  >
                                    <i className="fas fa-lock" /> Requires{' '}
                                    {pauseCheck.originalSignatoryRole} Approval
                                  </span>
                                  <span className="cmp-pause-pending-hint">
                                    Requested by {pauseRequestedBy}
                                  </span>
                                </div>
                              )
                            ) : (
                              <button
                                type="button"
                                className="cmp-btn-secondary"
                                disabled={isSaving}
                                onClick={() => onFinalize('PAUSE')}
                              >
                                {isSaving ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin" /> Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-pause" /> Pause Campaign
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        ) : pauseRequestedBy ? (
                          <div className="cmp-pause-requested-status">
                            <span className="cmp-pause-pending-pill">
                              <i className="fas fa-clock" /> Pause Approval Pending
                            </span>
                            <span className="cmp-pause-pending-hint">
                              Requested by {pauseRequestedBy}
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="cmp-btn-secondary"
                            disabled={isSaving}
                            onClick={() => onFinalize('REQUEST_PAUSE')}
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Submitting Request...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-hand-paper" /> Request Pause Approval
                              </>
                            )}
                          </button>
                        )
                      ) : initialCampaign?.status === 'PAUSED' ? (
                        canDirectApprove ? (
                          <button
                            type="button"
                            className="cmp-btn-primary"
                            disabled={isSaving}
                            onClick={() => onFinalize('RESUME')}
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Resuming...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-play" /> Resume Campaign
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="cmp-pause-requested-status">
                            <span
                              className="cmp-pause-pending-pill"
                              style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                borderColor: '#cbd5e1',
                              }}
                            >
                              <i className="fas fa-pause-circle" /> Campaign Paused (Management Only
                              Resume)
                            </span>
                          </div>
                        )
                      ) : (
                        <>
                          <button
                            type="button"
                            className={`cmp-btn-primary cmp-btn-activate ${
                              approvedGates.size === 4 && isPolicyCertified && hasLeads && !isSaving
                                ? 'ready'
                                : 'disabled'
                            }`}
                            disabled={
                              isSaving || approvedGates.size < 4 || !isPolicyCertified || !hasLeads
                            }
                            onClick={() => onFinalize('DIRECT_ACTIVATE')}
                          >
                            {isSaving ? (
                              <>
                                <i className="fas fa-spinner fa-spin" /> Saving to Database...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-rocket" /> Create & Activate Campaign
                              </>
                            )}
                          </button>
                          {hasLeads && isChannelSupported && (
                            <div className="cmp-activate-channel-hint-wrap">
                              <div className="cmp-activate-channel-hint">
                                <i
                                  className={
                                    /whatsapp/i.test(firstStepChannel)
                                      ? 'fab fa-whatsapp'
                                      : 'fas fa-envelope'
                                  }
                                />
                                <span>
                                  Activating will launch Step 1 ({firstStepChannel}) outreach
                                  composer with AI copy
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="cmp-btn-secondary"
                        disabled={isSaving}
                        onClick={() => onFinalize('SAVE_DRAFT')}
                      >
                        {isSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin" /> Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save" /> Save as Draft
                          </>
                        )}
                      </button>
                      <div className="cmp-gov-status-hint">
                        {!hasLeads ? (
                          <span className="text-danger">
                            • Target leads required (0 imported in Step 2)
                          </span>
                        ) : !hasCompleteMatrix ? (
                          <span className="text-danger">• Step 3 Persona Matrix incomplete</span>
                        ) : approvedGates.size < 4 ? (
                          <span>• {4 - approvedGates.size}/4 Gates required</span>
                        ) : !isPolicyCertified ? (
                          <span>• Check policy certification to activate</span>
                        ) : (
                          <span className="text-success">✓ Governance & Attestation Complete</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`cmp-btn-primary cmp-btn-activate ${
                          approvedGates.size === 4 &&
                          isPolicyCertified &&
                          hasLeads &&
                          hasCompleteMatrix &&
                          !isSaving
                            ? 'ready'
                            : 'disabled'
                        }`}
                        disabled={
                          isSaving ||
                          approvedGates.size < 4 ||
                          !isPolicyCertified ||
                          !hasLeads ||
                          !hasCompleteMatrix
                        }
                        onClick={() => onFinalize('DIRECT_ACTIVATE')}
                      >
                        {isSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin" /> Saving to Database...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-rocket" /> Create & Activate Campaign
                          </>
                        )}
                      </button>
                      {hasLeads && hasCompleteMatrix && isChannelSupported && (
                        <div className="cmp-activate-channel-hint-wrap">
                          <div className="cmp-activate-channel-hint">
                            <i
                              className={
                                /whatsapp/i.test(firstStepChannel)
                                  ? 'fab fa-whatsapp'
                                  : 'fas fa-envelope'
                              }
                            />
                            <span>
                              Activating will launch Step 1 ({firstStepChannel}) outreach composer
                              with AI copy
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : isPendingApproval ? (
            /* ── Member View: Campaign Proposal Already Submitted & Under Review ── */
            <div className="cmp-gov-card" style={{ marginTop: '20px' }}>
              <div className="cmp-gov-header">
                <div className="cmp-gov-header-left">
                  <div
                    className="cmp-gov-icon-wrap"
                    style={{ background: '#eff6ff', color: '#2563eb' }}
                  >
                    <i className="fas fa-hourglass-half" />
                  </div>
                  <div>
                    <h3 className="cmp-gov-title">
                      Campaign Proposal Submitted &amp; Under Executive Review
                    </h3>
                    <p className="cmp-gov-sub">
                      Your campaign strategy blueprint has been submitted. Workspace Owners and
                      Sales Managers are currently conducting executive compliance review.
                    </p>
                  </div>
                </div>
                <div className="cmp-gov-header-right">
                  <span className="cmp-gov-badge pending">
                    <i className="fas fa-clock" /> In Executive Review
                  </span>
                </div>
              </div>

              {/* User Profile Card */}
              <div className="cmp-gov-user-row">
                <div className="cmp-gov-user-avatar">
                  {currentUserName
                    ? currentUserName
                        .split(' ')
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()
                    : 'U'}
                </div>
                <div className="cmp-gov-user-info">
                  <div className="cmp-gov-user-name-line">
                    <strong className="cmp-gov-user-name">
                      {currentUserName || 'Team Member'}
                    </strong>
                    <span className="cmp-gov-role-pill">
                      <i className="fas fa-user" /> {userRoleLabel || 'Sales Member'}
                    </span>
                  </div>
                  <span className="cmp-gov-user-meta">
                    Campaign Proposer • Submitted for Executive Sign-off • Tenant:{' '}
                    {workspaceName || 'Lead Huntrix Production Workspace'}
                  </span>
                </div>
              </div>

              {/* Informational Review Notice Banner */}
              <div
                className="cmp-member-info-banner"
                style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}
              >
                <i className="fas fa-shield-alt" style={{ color: '#2563eb' }} />
                <div>
                  <strong style={{ color: '#1e3a8a' }}>Governance Review in Progress:</strong>
                  <p style={{ color: '#1e40af' }}>
                    This proposal is awaiting executive approval from your Workspace Owner or Sales
                    Manager across the 4 required governance gates (Brand Voice, Commercial
                    Feasibility, Technical Deliverability, and Malaysia PDPA Statutory Compliance).
                    No additional action is required from you at this time.
                  </p>
                </div>
              </div>

              {/* 4 Gates Status Checklist (Read-Only) */}
              <div className="cmp-member-readiness-box">
                <h5 className="cmp-member-readiness-title">
                  <i className="fas fa-tasks" /> Governance Gates Review Status
                </h5>
                <div className="cmp-member-readiness-grid">
                  <div
                    className={`cmp-member-readiness-item ${approvedGates.has('brand_voice') ? 'ready' : 'pending'}`}
                  >
                    <i
                      className={
                        approvedGates.has('brand_voice') ? 'fas fa-check-circle' : 'fas fa-clock'
                      }
                    />
                    <span>
                      1. Brand Voice:{' '}
                      {approvedGates.has('brand_voice') ? 'Approved ✓' : 'Awaiting Sign-off'}
                    </span>
                  </div>
                  <div
                    className={`cmp-member-readiness-item ${approvedGates.has('commercial') ? 'ready' : 'pending'}`}
                  >
                    <i
                      className={
                        approvedGates.has('commercial') ? 'fas fa-check-circle' : 'fas fa-clock'
                      }
                    />
                    <span>
                      2. Commercial &amp; Budget:{' '}
                      {approvedGates.has('commercial') ? 'Approved ✓' : 'Awaiting Sign-off'}
                    </span>
                  </div>
                  <div
                    className={`cmp-member-readiness-item ${approvedGates.has('pdpa_privacy') ? 'ready' : 'pending'}`}
                  >
                    <i
                      className={
                        approvedGates.has('pdpa_privacy') ? 'fas fa-check-circle' : 'fas fa-clock'
                      }
                    />
                    <span>
                      3. Legal &amp; PDPA Compliance:{' '}
                      {approvedGates.has('pdpa_privacy') ? 'Approved ✓' : 'Awaiting Sign-off'}
                    </span>
                  </div>
                  <div
                    className={`cmp-member-readiness-item ${approvedGates.has('compliance_deliverability') ? 'ready' : 'pending'}`}
                  >
                    <i
                      className={
                        approvedGates.has('compliance_deliverability')
                          ? 'fas fa-check-circle'
                          : 'fas fa-clock'
                      }
                    />
                    <span>
                      4. Technical Deliverability:{' '}
                      {approvedGates.has('compliance_deliverability')
                        ? 'Approved ✓'
                        : 'Awaiting Sign-off'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Member Submitted State Footer Actions */}
              <div className="cmp-gov-footer-actions">
                <div className="cmp-gov-status-hint">
                  <span className="text-muted">
                    <i className="fas fa-lock" /> Proposal submitted • Currently locked for
                    executive review
                  </span>
                </div>

                <div
                  className="cmp-gov-req-member-status-pill"
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  <i className="fas fa-hourglass-half" /> Proposal Submitted (Under Review)
                </div>
              </div>
            </div>
          ) : (
            /* ── Member Proposal & Executive Review Routing View ── */
            <div className="cmp-gov-card" style={{ marginTop: '20px' }}>
              <div className="cmp-gov-header">
                <div className="cmp-gov-header-left">
                  <div className="cmp-gov-icon-wrap blue">
                    <i className="fas fa-paper-plane" />
                  </div>
                  <div>
                    <h3 className="cmp-gov-title">
                      {initialCampaign?.status === 'CHANGES_REQUESTED'
                        ? 'Resubmit Revised Campaign for Approval'
                        : 'Campaign Proposal & Executive Review Routing'}
                    </h3>
                    <p className="cmp-gov-sub">
                      {initialCampaign?.status === 'CHANGES_REQUESTED'
                        ? `Submit your updated campaign addressing feedback from ${initialCampaign.revisionRequestedBy || 'your Sales Manager'} for executive compliance verification.`
                        : 'Submit your finalized campaign strategy blueprint for executive compliance verification and approval.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* User Profile Card */}
              <div className="cmp-gov-user-row">
                <div className="cmp-gov-user-avatar">
                  {currentUserName
                    ? currentUserName
                        .split(' ')
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()
                    : 'U'}
                </div>
                <div className="cmp-gov-user-info">
                  <div className="cmp-gov-user-name-line">
                    <strong className="cmp-gov-user-name">
                      {currentUserName || 'Team Member'}
                    </strong>
                    <span className="cmp-gov-role-pill">
                      <i className="fas fa-user" /> {userRoleLabel || 'Sales Member'}
                    </span>
                  </div>
                  <span className="cmp-gov-user-meta">
                    Campaign Proposer • Tenant:{' '}
                    {workspaceName || 'Lead Huntrix Production Workspace'}
                  </span>
                </div>
              </div>

              {/* Workflow Notice Banner */}
              <div className="cmp-member-info-banner">
                <i className="fas fa-info-circle" />
                <div>
                  <strong>
                    {initialCampaign?.status === 'CHANGES_REQUESTED'
                      ? 'Revision Workflow Notice:'
                      : 'Governance Workflow Notice:'}
                  </strong>
                  <p>
                    {initialCampaign?.status === 'CHANGES_REQUESTED'
                      ? `You have updated this campaign according to revision feedback from ${initialCampaign.revisionRequestedBy || 'your Sales Manager'}. Resubmitting will notify your Workspace Owner and Manager to conduct final compliance verification and approve the campaign.`
                      : 'As a team member, submitting this campaign routes it directly to Workspace Owners, Administrators, and Sales Managers for compliance verification (Brand Voice, Commercial Feasibility, Technical Deliverability, and Malaysia PDPA Compliance) prior to outbound activation.'}
                  </p>
                </div>
              </div>

              {/* Blueprint Readiness Checklist */}
              <div className="cmp-member-readiness-box">
                <h5 className="cmp-member-readiness-title">
                  <i className="fas fa-clipboard-check" /> Campaign Blueprint Readiness
                </h5>
                <div className="cmp-member-readiness-grid">
                  <div
                    className={`cmp-member-readiness-item ${hasCompleteMatrix ? 'ready' : 'warning'}`}
                  >
                    <i
                      className={
                        hasCompleteMatrix ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'
                      }
                    />
                    <span>
                      {hasCompleteMatrix
                        ? 'Strategy & Value Proposition Matrix Configured'
                        : 'Incomplete Persona Messaging Matrix (Step 3 has empty fields)'}
                    </span>
                  </div>
                  <div className="cmp-member-readiness-item ready">
                    <i className="fas fa-check-circle" />
                    <span>ICP & Persona Target Cohorts Defined</span>
                  </div>
                  <div className="cmp-member-readiness-item ready">
                    <i className="fas fa-check-circle" />
                    <span>Multi-Touch Outreach Cadence Structured</span>
                  </div>
                  <div className={`cmp-member-readiness-item ${hasLeads ? 'ready' : 'warning'}`}>
                    <i
                      className={hasLeads ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'}
                    />
                    <span>
                      {hasLeads
                        ? `${importedLeadsCount} Target Leads Imported from Pipeline`
                        : '0 Target Leads Imported in Step 2'}
                    </span>
                  </div>
                </div>
              </div>

              {!hasLeads && (
                <div className="cmp-gov-no-leads-banner" style={{ marginTop: '16px' }}>
                  <div className="cmp-gov-no-leads-icon">
                    <i className="fas fa-exclamation-triangle" />
                  </div>
                  <div className="cmp-gov-no-leads-content">
                    <strong>Target Audience Required Before Submission</strong>
                    <p>
                      This campaign currently has 0 imported target leads. To enable outreach
                      touchpoints, please import at least 1 lead from the CRM pipeline in Step 2.
                    </p>
                    {onNavigateToStep && (
                      <button
                        type="button"
                        className="cmp-btn-secondary cmp-btn-jump-step2"
                        onClick={() => onNavigateToStep(2)}
                      >
                        <i className="fas fa-file-import" /> Go to Step 2 to Import Leads
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!hasCompleteMatrix && hasLeads && (
                <div
                  className="cmp-gov-no-leads-banner"
                  style={{ marginTop: '16px', background: '#fffbeb', borderColor: '#fde68a' }}
                >
                  <div
                    className="cmp-gov-no-leads-icon"
                    style={{ background: '#fef3c7', color: '#d97706' }}
                  >
                    <i className="fas fa-layer-group" />
                  </div>
                  <div className="cmp-gov-no-leads-content">
                    <strong style={{ color: '#92400e' }}>
                      Persona Messaging Matrix Required Before Submission
                    </strong>
                    <p style={{ color: '#78350f' }}>
                      This campaign contains empty persona messaging frameworks in Step 3. Please
                      fill in all required fields or generate them with AI in Step 3 before
                      submitting.
                    </p>
                    {onNavigateToStep && (
                      <button
                        type="button"
                        className="cmp-btn-secondary cmp-btn-jump-step2"
                        onClick={() => onNavigateToStep(3)}
                      >
                        <i className="fas fa-magic" /> Go to Step 3 to Complete Matrix
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Member Footer Actions */}
              <div className="cmp-gov-footer-actions">
                <button
                  type="button"
                  className="cmp-btn-secondary"
                  disabled={isSaving}
                  onClick={() => onFinalize(isEditMode ? 'SAVE_CHANGES' : 'SAVE_DRAFT')}
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" /> Save as Draft
                    </>
                  )}
                </button>

                <div className="cmp-gov-status-hint">
                  {!hasLeads ? (
                    <span className="text-danger">
                      • Target leads required (0 imported in Step 2)
                    </span>
                  ) : !hasCompleteMatrix ? (
                    <span className="text-danger">
                      • Step 3 Persona Matrix incomplete (empty fields detected)
                    </span>
                  ) : (
                    <span className="text-success">✓ Blueprint Ready for Review</span>
                  )}
                </div>

                <button
                  type="button"
                  className={`cmp-btn-primary cmp-btn-activate ${hasLeads && hasCompleteMatrix && !isSaving ? 'ready' : 'disabled'}`}
                  disabled={isSaving || !hasLeads || !hasCompleteMatrix}
                  onClick={() => onFinalize('SUBMIT_APPROVAL')}
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane" />{' '}
                      {initialCampaign?.status === 'CHANGES_REQUESTED'
                        ? 'Resubmit Revised Campaign for Approval'
                        : 'Submit Campaign for Executive Approval'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Cryptographic Certificate Verification Modal */}
      <CertificateVerificationModal
        isOpen={isVerifyModalOpen}
        onClose={() => setIsVerifyModalOpen(false)}
        formData={formData}
        initialCampaign={initialCampaign}
        approvedGates={approvedGates}
        certId={certId}
        certHash={certHash}
        signedBy={cleanApproverName}
        approverRole={approverRole}
        certifiedAt={formattedApprovedDate}
        campaignName={
          formData?.title ||
          formData?.name ||
          initialCampaign?.name ||
          'Lead Huntrix Growth Campaign'
        }
        workspaceName={workspaceName || 'Lead Huntrix Production Workspace'}
        jurisdiction="Malaysia (PDPA 2010)"
      />
    </div>
  );
}
